<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>
      <div class="flex flex-col space-y-6">
        <Header :text="$tc('sac.visualExplorer', 2)" />

        <v-card class="mb-6 explorer-card">
          <v-card-title class="explorer-title">
            <v-icon left size="24" color="primary">mdi-graph</v-icon>
            Security Assurance Case Explorer
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewType" mandatory>
              <v-btn value="graph">
                <v-icon left>mdi-graph-outline</v-icon>
                Graph
              </v-btn>
              <v-btn value="tree">
                <v-icon left>mdi-file-tree</v-icon>
                Tree
              </v-btn>
            </v-btn-toggle>
          </v-card-title>

          <v-card-text>
            <!-- Case Selection -->
            <v-select
              v-model="selectedCase"
              :items="securityCases"
              item-text="name"
              item-value="id"
              label="Select Security Assurance Case"
              outlined
              @change="loadCaseData"
              class="mb-4"
            ></v-select>

            <!-- Options Panel -->
            <v-expansion-panels v-model="optionsPanel" class="mb-4">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <v-icon left>mdi-tune</v-icon>
                  Visualization Options
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-switch
                        v-model="showLabels"
                        label="Show Labels"
                        color="primary"
                        hide-details
                        inset
                      ></v-switch>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-switch
                        v-model="showDetails"
                        label="Show Details"
                        color="primary"
                        hide-details
                        inset
                      ></v-switch>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-switch
                        v-model="highlightConnections"
                        label="Highlight Connections"
                        color="primary"
                        hide-details
                        inset
                      ></v-switch>
                    </v-col>
                  </v-row>

                  <v-row class="mt-4">
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="layoutType"
                        :items="layoutOptions"
                        label="Layout"
                        outlined
                        dense
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-slider
                        v-model="zoomLevel"
                        label="Zoom"
                        thumb-label
                        min="50"
                        max="150"
                        thumb-size="24"
                        hide-details
                      ></v-slider>
                    </v-col>
                  </v-row>

                  <v-row class="mt-4">
                    <v-col cols="12">
                      <v-btn color="primary" @click="refreshVisualization" class="mr-2">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh Visualization
                      </v-btn>
                      <v-btn color="secondary" outlined @click="resetOptions">
                        <v-icon left>mdi-restore</v-icon>
                        Reset Options
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Visualization Container -->
            <v-card
              height="600"
              class="visualization-container"
              :class="{ 'graph-view': viewType === 'graph', 'tree-view': viewType === 'tree' }"
              outlined
            >
              <div v-if="loading" class="d-flex justify-center align-center fill-height">
                <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
              </div>

              <div
                v-else-if="!selectedCase"
                class="d-flex justify-center align-center fill-height flex-column"
              >
                <v-icon size="64" color="grey lighten-1">mdi-chart-network</v-icon>
                <div class="mt-4 text-subtitle-1 grey--text text--darken-1">
                  Select a Security Assurance Case to visualize
                </div>
              </div>

              <div
                v-else-if="noData"
                class="d-flex justify-center align-center fill-height flex-column"
              >
                <v-icon size="64" color="grey lighten-1">mdi-alert</v-icon>
                <div class="mt-4 text-subtitle-1 grey--text text--darken-1">
                  No data available for visualization
                </div>
                <v-btn color="primary" class="mt-4" :to="{ name: 'Claims' }">
                  Create Claims
                </v-btn>
              </div>

              <div v-else id="visualization-graph" class="fill-height"></div>
            </v-card>

            <!-- Legend -->
            <v-card class="mt-4 legend-card" outlined>
              <v-card-text class="py-2">
                <div class="d-flex flex-wrap align-center">
                  <div class="legend-item">
                    <v-avatar size="16" color="primary" class="mr-2"></v-avatar>
                    <span>Claim</span>
                  </div>
                  <div class="legend-item">
                    <v-avatar size="16" color="secondary" class="mr-2"></v-avatar>
                    <span>Sub-Claim</span>
                  </div>
                  <div class="legend-item">
                    <v-avatar size="16" color="success" class="mr-2"></v-avatar>
                    <span>Evidence</span>
                  </div>
                  <div class="legend-item">
                    <v-avatar size="16" color="error" class="mr-2"></v-avatar>
                    <span>Issue</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-line mr-2"></div>
                    <span>Relationship</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>

          <v-card-actions class="pa-4 pt-0">
            <v-btn color="grey" @click="$router.push({ name: 'SecurityAssuranceCase' })">
              <v-icon left>mdi-arrow-left</v-icon>
              Back
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="success" @click="exportVisualization">
              <v-icon left>mdi-export</v-icon>
              Export
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import Header from '@/components/helpers/Header';
import Loader from '@/components/helpers/Loader';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import securityAssuranceCaseApi from '../api/securityAssuranceCaseApi';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'VisualExplorer',
  components: {
    Header,
    Loader,
    Breadcrumbs,
  },
  data() {
    return {
      loading: false,
      viewType: 'graph',
      selectedCase: null,
      securityCases: [],
      caseData: null,
      noData: false,

      // Visualization options
      optionsPanel: [0], // Expanded by default
      showLabels: true,
      showDetails: true,
      highlightConnections: false,
      layoutType: 'hierarchical',
      layoutOptions: ['hierarchical', 'force-directed', 'radial', 'circular'],
      zoomLevel: 100,

      // Visualization instance
      network: null,
    };
  },
  created() {
    this.fetchSecurityCases();
  },
  methods: {
    async fetchSecurityCases() {
      this.loading = true;
      try {
        const { data } = await securityAssuranceCaseApi.all();
        this.securityCases = data.map(sac => ({
          id: sac.id,
          name: sac.name,
          description: sac.description,
        }));

        // Auto-select first case if available
        if (this.securityCases.length > 0) {
          this.selectedCase = this.securityCases[0].id;
          this.loadCaseData();
        }
      } catch (error) {
        console.error('Error fetching security cases:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.error-fetch'),
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    async loadCaseData() {
      if (!this.selectedCase) return;

      this.loading = true;
      try {
        // In a real implementation, this would fetch the structure data for visualization
        // Mocking the API call and data for demonstration
        const response = await securityAssuranceCaseApi.getById(this.selectedCase);
        this.caseData = response.data;

        // Check if there's data to visualize
        this.noData =
          !this.caseData ||
          !this.caseData.socRequirementLinks ||
          this.caseData.socRequirementLinks.length === 0;

        if (!this.noData) {
          this.$nextTick(() => {
            this.initializeVisualization();
          });
        }
      } catch (error) {
        console.error('Error loading case data:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.error-fetch'),
          color: 'error',
        });
        this.noData = true;
      } finally {
        this.loading = false;
      }
    },

    initializeVisualization() {
      // This would be implemented with a visualization library like vis.js or echarts
      // For now, just showing a placeholder implementation
      const container = document.getElementById('visualization-graph');

      if (container) {
        container.innerHTML = '';

        if (this.viewType === 'graph') {
          // Placeholder for graph visualization
          container.innerHTML = `
            <div class="placeholder-visualization">
              <div class="text-center">
                <div class="text-h6">Graph Visualization Placeholder</div>
                <div class="text-body-1 mt-2">
                  A real implementation would use a graph visualization library to show the security assurance case structure.
                </div>
              </div>
              <div class="nodes-container mt-6">
                <div class="node node-claim">Claim</div>
                <div class="node-connector"></div>
                <div class="node node-subclaim">Sub-Claim</div>
                <div class="node-connector"></div>
                <div class="node node-evidence">Evidence</div>
              </div>
            </div>
          `;
        } else {
          // Placeholder for tree visualization
          container.innerHTML = `
            <div class="placeholder-visualization">
              <div class="text-center">
                <div class="text-h6">Tree Visualization Placeholder</div>
                <div class="text-body-1 mt-2">
                  A real implementation would use a tree visualization library to show the security assurance case hierarchy.
                </div>
              </div>
              <div class="tree-container mt-6">
                <ul class="tree">
                  <li>
                    <span class="tree-node-claim">Claim</span>
                    <ul>
                      <li>
                        <span class="tree-node-subclaim">Sub-Claim 1</span>
                        <ul>
                          <li><span class="tree-node-evidence">Evidence 1</span></li>
                          <li><span class="tree-node-evidence">Evidence 2</span></li>
                        </ul>
                      </li>
                      <li>
                        <span class="tree-node-subclaim">Sub-Claim 2</span>
                        <ul>
                          <li><span class="tree-node-evidence">Evidence 3</span></li>
                        </ul>
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          `;
        }
      }
    },

    refreshVisualization() {
      this.initializeVisualization();
    },

    resetOptions() {
      this.showLabels = true;
      this.showDetails = true;
      this.highlightConnections = false;
      this.layoutType = 'hierarchical';
      this.zoomLevel = 100;
      this.refreshVisualization();
    },

    exportVisualization() {
      // This would export the current visualization to an image file
      EventBus.$emit('snackbar', {
        text: 'Export functionality would save this visualization as an image',
        color: 'info',
      });
    },
  },
};
</script>

<style scoped>
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
}

.breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}

.explorer-card {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

.explorer-title {
  background: linear-gradient(135deg, #00405e 0%, #006494 100%);
  color: white;
  font-weight: 600;
}

.visualization-container {
  background-color: #fafafa;
  transition: all 0.3s ease;
  position: relative;
}

.graph-view {
  background-color: #f5f7fa;
}

.tree-view {
  background-color: #f8f9fa;
}

.legend-card {
  background-color: #f5f7fa;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-right: 20px;
  margin-bottom: 8px;
}

.legend-line {
  height: 2px;
  width: 16px;
  background-color: #666;
}

/* Placeholder visualization styles */
.placeholder-visualization {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
}

.nodes-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.node {
  padding: 10px 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
}

.node-claim {
  background-color: #1976d2;
  color: white;
}

.node-subclaim {
  background-color: #7b1fa2;
  color: white;
}

.node-evidence {
  background-color: #388e3c;
  color: white;
}

.node-connector {
  width: 50px;
  height: 2px;
  background-color: #666;
}

/* Tree visualization placeholder */
.tree {
  list-style: none;
  padding-left: 20px;
}

.tree li {
  position: relative;
  padding-left: 20px;
  margin-bottom: 10px;
}

.tree li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 12px;
  width: 15px;
  height: 2px;
  background-color: #666;
}

.tree li::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #666;
}

.tree li:last-child::after {
  height: 13px;
}

.tree-node-claim,
.tree-node-subclaim,
.tree-node-evidence {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
}

.tree-node-claim {
  background-color: #1976d2;
  color: white;
}

.tree-node-subclaim {
  background-color: #7b1fa2;
  color: white;
}

.tree-node-evidence {
  background-color: #388e3c;
  color: white;
}

.tree ul {
  list-style: none;
  padding-left: 0;
  margin-top: 10px;
}
</style>
